import type { AxiosRequestConfig } from 'axios';
import { isAxiosError } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import { responseContract } from '../../lib';
import {
  type GetComparisonTableRequestDto,
  getComparisonTable,
} from './get-comparison-table';

export const GetStaffTrendsTableRequestDtoSchema = z.object({
  facilityId: z.number(),
  from: z.string(),
  to: z.string(),
  comparisonSet: z.enum(['INTERNAL', 'COMP']).optional(),
});

const MetricValueSchema = z.object({
  owned: z.number().nullable(),
  compSet: z.number().nullable(),
  deltaVsMarket: z.number().nullable(),
});

export const GetStaffTrendsTableResponseDtoSchema = z.object({
  periods: z.array(
    z.object({
      period: z.string(),
      dateRange: z.string(),
      staffingRatio: MetricValueSchema,
      averageTenure: MetricValueSchema,
      turnoverRate: MetricValueSchema,
    })
  ),
});

export type GetStaffTrendsTableRequestDto = z.infer<
  typeof GetStaffTrendsTableRequestDtoSchema
>;

export type GetStaffTrendsTableResponseDto = z.infer<
  typeof GetStaffTrendsTableResponseDtoSchema
>;

export const getStaffTrendsTable = async (
  params: GetStaffTrendsTableRequestDto,
  config?: AxiosRequestConfig<GetStaffTrendsTableRequestDto>
) => {
  const { facilityId, ...restParams } = params;

  try {
    return await api
      .get(`market-intelligence/facilities/${facilityId}/trends-table-staff`, {
        params: restParams,
        ...config,
      })
      .then(responseContract(GetStaffTrendsTableResponseDtoSchema));
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 404) {
      throw error;
    }

    const comparisonTable = await getComparisonTable(
      params as GetComparisonTableRequestDto,
      config as AxiosRequestConfig<GetComparisonTableRequestDto>
    );

    return {
      periods: comparisonTable.periods.map((period) => ({
        period: period.period,
        dateRange: period.dateRange,
        staffingRatio: period.staffingRatio,
        averageTenure: period.averageTenure,
        turnoverRate: period.turnoverRate,
      })),
    };
  }
};
